import { Injectable } from '@angular/core';
import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})
/**
 * Send telemetry and logs to a central monitoring service (Application Insights) for aggregation.
 */
export class MonitoringService {

  private appInsights: ApplicationInsights;

  /**
   * The minimum severity of logs to send to central monitoring
   * @private
   */
  private minimumSeverityLevel: SeverityLevel;

  initialise() {
    if (!environment.appInsightsInstrumentationKey) {
      console.warn("No valid instrumentation key set for Application Insights.");
      return;
    }

    // set a minimum level or fallback to warning
    // consider modifying this logic depending on load or requirements
    this.minimumSeverityLevel =
      environment.appInsightsMinimumLogLevel
        ? environment.appInsightsMinimumLogLevel
        : SeverityLevel.Warning;

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsightsInstrumentationKey,

        // Auto log Angular routing. This will:
        // 1. track page views
        //    Field "Name" will default to the page title at the time of the trace
        //    Field "refUri" will be populated
        //    Field "Load time" will be 0 (cannot be easily calculated in SPA)
        // 2. create a new Operation (Id) for each page view, which allows to correlate requests across frontend and all backend services.
        //    The "Operation Name" will default to the current url (location.pathname + hash)
        //    If this is disabled, every session will only have _one_ OperationId and _all_ requests will be correlated with each other.
        enableAutoRouteTracking: true,

        // Consider sending less samples depending on load
        // samplingPercentage: 100,

        // Track dependencies by attaching headers to requests
        enableCorsCorrelation: true,
        // correlationHeaderExcludedDomains: ["localhost:4200"]
      }
    });
    this.appInsights.loadAppInsights();
    this.appInsights.addTelemetryInitializer(item => {
      // Set name to correctly identifies traces
      // see https://docs.microsoft.com/en-us/azure/azure-monitor/app/app-map?tabs=javascript#set-or-override-cloud-role-name
      item.tags['ai.cloud.role'] = 'My Hygiene Platform Web'
    })
  }

  /**
   * Log an Exception.
   *
   * @param error The error object.
   * @param customDimensions Any additional properties and values to store with the trace.
   */
  logException(
    error: Error,
    customDimensions?: { [key: string]: any }
  ) {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.trackException({ exception: error, properties: customDimensions });
  }

  /**
   * Log a single trace Statement.
   *
   * Make sure to add any relevant structural data to `customDimensions`, so they can be indexed and queried by the monitoring provider.
   *
   * @example
   * this.monitoring.logTrace(`Queried data for object with id ${id}`, {ObjectId: id}, SeverityLevel.Information);
   *
   * @param message The log statement.
   * @param customDimensions Any additional properties and values to store with the trace.
   * @param severityLevel The severity of this trace.
   */
  logTrace(
    message: string,
    customDimensions?: { [key: string]: any },
    severityLevel?: SeverityLevel
  ) {
    if (!this.appInsights) {
      return;
    }

    // if severityLevel is specified, filter for minimum level
    if (severityLevel && severityLevel < this.minimumSeverityLevel) {
      return;
    }

    this.appInsights.trackTrace({ message: message, properties: customDimensions, severityLevel: severityLevel });
  }
}
